<template>
  <div class="mx-3">
    <v-row>
      <v-col
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <setup-holiday-holiday-detail-dialog-create 
          v-if="$hasPermissionTo(['create holiday details'])"
          :states="states"
          @created="loadData"
        />
      </v-col>
      <v-col
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >    
        <setup-holiday-holiday-detail-dialog-copy 
          v-if="$hasPermissionTo(['create holiday details'])"
          :states="states"
          :available-years="holidayYears"
          @created="handleDetailCopied"
        />
      </v-col>
      <v-spacer />

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-autocomplete
          v-model="yearFilter"
          clearable
          :items="holidayYears"
          label="Year Filter"
          placeholder="Filter by year"
        />
      </v-col>

      <v-col
        class="py-0"
        cols="12"
        lg="3"          
        md="12"
        sm="12"
        xl="2"
      >
        <v-text-field
          v-model="search"
          placeholder="Key in holiday name here"
          append-icon="search"
          label="Search holiday"
          hide-no-data
          hide-selected
          clearable 
        />
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredHolidays"
      :loading="tableLoading"
      :search="search"
      show-expand
      :single-expand="false"
    >
      <template v-slot:item.stateNames="{ item }">
        <div>
          <span class="px-3 pl-0 font-weight-bold">
            {{ item.stateNames }}
          </span>
        </div>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td />
           
        <v-treeview
          :items="item.states_departments"
          dense
        />
      </template>

      <template v-slot:item.action="{ item }">
        <setup-holiday-holiday-detail-dialog-assign
          v-if="$hasPermissionTo(['update holiday details'])"
          :states="states"
          :holiday-detail="item"
          @updated="loadData"
        />
        <setup-holiday-holiday-detail-dialog-delete
          v-if="$hasPermissionTo(['delete holiday details'])"
          :holiday-detail="item"
          @deleted="loadData"
        />
        <span class="mx-1" />
      </template>
    </v-data-table>
  </div>
</template>

<script>
  export default {
    components: {
      SetupHolidayHolidayDetailDialogCopy: () => import('@/components/hris/setup/holiday/holiday-detail/DialogCopy'),
      SetupHolidayHolidayDetailDialogCreate: () => import('@/components/hris/setup/holiday/holiday-detail/DialogCreate'),
      SetupHolidayHolidayDetailDialogAssign: () => import('@/components/hris/setup/holiday/holiday-detail/DialogAssign'),
      SetupHolidayHolidayDetailDialogDelete: () => import('@/components/hris/setup/holiday/holiday-detail/DialogDelete'),
    },
    data () {
      return {
        headers: [
          {
            text: 'Holiday Name',
            align: 'left',
            value: 'holiday.name',
          },
          {
            text: 'States',
            value: 'stateNames',
            align: 'center',
            sortable: false,
          },
          {
            text: 'Date',
            value: 'date',
            align: 'center',
          },
          {
            text: '',
            value: 'action',
            align: 'left',
          },
        ],
        search: '',
        tableLoading: true,
        states: [],
        holidayDetails: [],
        yearFilter: (new Date()).getFullYear(),
        stateFilter: null,
        holidayTypes: [
          { value: 'all', text: 'All' },
          { value: 'regional', text: 'Regional' },
          { value: 'national', text: 'National' },
        ],
      }
    },
    computed: {
      holidayYears () {
        let years = this.holidayDetails.map((detail) => {
          return this.$moment(detail.date, 'YYYY-MM-DD').year()   
        })

        return [...new Set(years)]
      },
      filteredHolidays () {
        return this.holidayDetails
          .filter((detail) => {
            if (!this.yearFilter) return true

            return this.$moment(detail.date, 'YYYY-MM-DD').year() === this.yearFilter
          })
          .filter((detail) => {
            if (!this.stateFilter) return true

            let found = detail.holiday_states.find(state => {
              return state.state.id === this.stateFilter
            })

            return !!found
          })
      },
    },
    created () {
      this.loadData()
      this.loadStates()
    },
    methods: {
      loadData () {
        this.tableLoading = true
        
        this.$api.hrisHolidayDetails().list('?filter[min_year]=' + (new Date().getFullYear() - 2))
          .then((response) => {
            response.data.forEach((detail) => {
              detail.states_departments = this.groupDeparmentsByState(detail.holiday_states)
              detail.states_departments.forEach(stateDepartment => {
                stateDepartment.children = this.changeToTreeStructure(stateDepartment.departments)
              })
    
              detail.stateNames = this.getStateCodes(detail.holiday_states)
              if (detail.stateNames.split(',').length === 16) {
                detail.stateNames = 'All'
              } else if (detail.stateNames.split(',').length === 0) {
                detail.stateNames = 'None'
              }
              detail.departmentIds = this.getDepartmentIds(detail.holiday_states)
            })       
         
            this.holidayDetails = _.orderBy(response.data, ['date'], ['asc'])
            this.tableLoading = false
          })
      },

      loadStates () {
        this.$api.hrisStates().list()
          .then((res) => {
            this.states = res.data
          })
      },

      handleDetailCopied (year) {
        this.setYearFilter(year)
        this.loadData()
        this.loadStates()
      },

      setYearFilter (year) {
        this.yearFilter = year
      },

      groupDeparmentsByState (states) {
        let stateDepartments = _.chain(states)
          .groupBy('state.name')
          .map((value, key) => 
            ({ name: key, departments: value })
          )
          .value()
      
        return stateDepartments
      },

      getStateCodes (states) {
        let codes = ''
        states.forEach((val, idx, src) => {          
          if (!codes.includes(val.state.code)) {  
            codes += val.state.code + ', '
          }
        })
        return codes.substring(0, codes.length - 2)
      },      

      changeToTreeStructure (data) {
        let departments = []

        data.forEach(val => {
          if (val.department) {
            departments.push({ name: val.department.name })
          }
        })
        return departments
      },

      getDepartmentIds (states) {
        let ids = []

        states.forEach(val => {
          if (val.department) {
            if (!ids.includes(val.department.id)) {
              ids.push(val.department.id)
            }
          }
        })
        return ids
      },  
    },
  }
</script>
